/**
 * AgentsTab — 테넌트별 AIFY Agent 컨테이너의 상태 모니터링 + start/stop.
 *
 * MVP 스콥: 리스트 + 상태 Chip + 토글 버튼 + 새로고침.
 * 추가 서브패널(ConversationViewer / RulesEditor / TenantAdminSection / ...)은 후속 세션에서 포팅.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Dialog } from "@mui/material";

import * as tenantsApi from "../api/tenants";
import type { Agent, AgentStatus } from "../api/types";
import { TenantDetailDialog } from "./TenantDetailDialog";
import { TenantFormDialog } from "./TenantFormDialog";

const TENANTS_SEED: Omit<Agent, "status">[] = [
  { tenantCode: "c0159c0", name: "Donggu Biopharma", nameKorean: "동구바이오제약", emoji: "💊", category: "customer" },
  { tenantCode: "c1d6620", name: "GC Green Cross", nameKorean: "녹십자", emoji: "🏥", category: "customer" },
  { tenantCode: "c373710", name: "Kdrive", nameKorean: "Kdrive", emoji: "📁", category: "customer" },
  { tenantCode: "c8cd350", name: "LNK Wellness", nameKorean: "LNK웰니스", emoji: "🧘", category: "customer" },
  { tenantCode: "caaa3b0", name: "JeilSYC", nameKorean: "제일에스와이씨", emoji: "🏭", category: "customer" },
  { tenantCode: "cf52600", name: "ATECH", nameKorean: "에이텍", emoji: "⚙️", category: "customer" },
  { tenantCode: "cff7780", name: "WorkersHi", nameKorean: "워커스하이", emoji: "👷", category: "customer" },
];

const STATUS_COLOR: Record<AgentStatus, "success" | "warning" | "default"> = {
  Running: "success",
  Starting: "warning",
  Stopped: "default",
};

export function AgentsTab() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState<Set<string>>(new Set());
  const [detailAgent, setDetailAgent] = useState<Agent | null>(null);
  const [tenantFormOpen, setTenantFormOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Agent | null>(null);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const fetched = await tenantsApi.fetchTenants();
      setAgents(fetched);
      setError(null);
    } catch (e) {
      setAgents(TENANTS_SEED.map((t) => ({ ...t, status: "Stopped" as AgentStatus })));
      setError(e instanceof Error ? e.message : "API 호출에 실패했습니다. 캐시된 목록을 표시합니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Starting 상태인 에이전트가 있으면 5초마다 폴링
  useEffect(() => {
    const hasStarting = agents.some((a) => a.status === "Starting");
    if (hasStarting && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        try {
          const fetched = await tenantsApi.fetchTenants();
          setAgents(fetched);
        } catch {
          /* 폴링 에러는 무시 */
        }
      }, 5000);
    }
    if (!hasStarting && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [agents]);

  const toggle = async (tenantCode: string, currentStatus: AgentStatus) => {
    setToggling((prev) => new Set(prev).add(tenantCode));
    try {
      if (currentStatus === "Running") {
        await tenantsApi.stopAgent(tenantCode);
        setAgents((prev) =>
          prev.map((a) => (a.tenantCode === tenantCode ? { ...a, status: "Stopped" } : a)),
        );
      } else {
        await tenantsApi.startAgent(tenantCode);
        setAgents((prev) =>
          prev.map((a) => (a.tenantCode === tenantCode ? { ...a, status: "Starting" } : a)),
        );
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "에이전트 상태 변경에 실패했습니다.");
    } finally {
      setToggling((prev) => {
        const next = new Set(prev);
        next.delete(tenantCode);
        return next;
      });
    }
  };

  const openDeleteDialog = (agent: Agent) => {
    setDeleteTarget(agent);
    setDeleteConfirmInput("");
  };

  const closeDeleteDialog = () => {
    if (deleteSubmitting) return;
    setDeleteTarget(null);
    setDeleteConfirmInput("");
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmInput !== deleteTarget.tenantCode) return;
    setDeleteSubmitting(true);
    try {
      await tenantsApi.deleteTenant(deleteTarget.tenantCode);
      setAgents((prev) => prev.filter((a) => a.tenantCode !== deleteTarget.tenantCode));
      setError(null);
      setDeleteTarget(null);
      setDeleteConfirmInput("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "테넌트 삭제에 실패했습니다.");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const runningCount = agents.filter((a) => a.status === "Running").length;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Stack direction="row" spacing={1} alignItems="center" data-testid="tab-summary-chips">
          <Typography variant="subtitle1" fontWeight={500}>
            테넌트 상태
          </Typography>
          <Chip size="small" label={`Running ${runningCount}/${agents.length}`} color={runningCount > 0 ? "success" : "default"} />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => {
              setEditingAgent(null);
              setTenantFormOpen(true);
            }}
          >
            테넌트 추가
          </Button>
          <Tooltip title="새로고침">
            <IconButton onClick={load} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {error && <Alert severity="warning">{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }}></TableCell>
              <TableCell>테넌트</TableCell>
              <TableCell>코드</TableCell>
              <TableCell>상태</TableCell>
              <TableCell align="right" sx={{ width: 280 }}>액션</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agents.map((agent) => {
              const busy = toggling.has(agent.tenantCode);
              const running = agent.status === "Running";
              const starting = agent.status === "Starting";
              return (
                <TableRow key={agent.tenantCode} hover>
                  <TableCell sx={{ fontSize: "1.2rem" }}>{agent.emoji}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{agent.nameKorean}</Typography>
                    <Typography variant="caption" color="text.secondary">{agent.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                      {agent.tenantCode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={agent.status}
                      color={STATUS_COLOR[agent.status]}
                      variant={running ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="편집">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingAgent(agent);
                            setTenantFormOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="삭제">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openDeleteDialog(agent)}
                            disabled={running || starting}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => setDetailAgent(agent)}
                      >
                        상세
                      </Button>
                      <Button
                        size="small"
                        variant={running ? "outlined" : "contained"}
                        color={running ? "error" : "success"}
                        startIcon={busy ? <CircularProgress size={14} /> : running ? <StopIcon /> : <PlayArrowIcon />}
                        onClick={() => toggle(agent.tenantCode, agent.status)}
                        disabled={busy || starting}
                      >
                        {running ? "Stop" : starting ? "Starting" : "Start"}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {detailAgent && (
        <TenantDetailDialog
          open={!!detailAgent}
          onClose={() => setDetailAgent(null)}
          tenantCode={detailAgent.tenantCode}
          tenantName={detailAgent.nameKorean}
          emoji={detailAgent.emoji}
        />
      )}

      <TenantFormDialog
        open={tenantFormOpen}
        onClose={() => setTenantFormOpen(false)}
        onSaved={load}
        editingAgent={editingAgent}
      />

      <Dialog open={!!deleteTarget} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "error.main", color: "white" }}>
          테넌트 삭제 확인
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {deleteTarget && (
            <Stack spacing={2}>
              <Alert severity="warning">
                k8s Deployment · Service · ConfigMap 행이 모두 제거됩니다. 되돌릴 수 없습니다.
              </Alert>
              <Box sx={{ bgcolor: "grey.100", p: 2, borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>이름</strong>: {deleteTarget.nameKorean} ({deleteTarget.name})
                </Typography>
                <Typography variant="body2">
                  <strong>코드</strong>: <code>{deleteTarget.tenantCode}</code>
                </Typography>
                <Typography variant="body2">
                  <strong>상태</strong>: {deleteTarget.status}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                확인을 위해 아래에 테넌트 코드 <code>{deleteTarget.tenantCode}</code> 를 정확히 입력해주세요.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                size="small"
                value={deleteConfirmInput}
                onChange={(e) => setDeleteConfirmInput(e.target.value)}
                placeholder={deleteTarget.tenantCode}
                disabled={deleteSubmitting}
              />
              <Typography variant="caption" color="text.secondary">
                GCS 버킷의 데이터는 이 작업으로 삭제되지 않습니다. 필요 시 별도 정리하세요.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteSubmitting}>
            취소
          </Button>
          <Button
            onClick={submitDelete}
            color="error"
            variant="contained"
            disabled={
              deleteSubmitting ||
              !deleteTarget ||
              deleteConfirmInput !== deleteTarget.tenantCode
            }
            startIcon={deleteSubmitting ? <CircularProgress size={14} /> : <DeleteIcon />}
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
